import React from 'react'
import ReactDOM from 'react-dom'
import { Icon } from './icons'
import { avatarColor, initials } from './format'
import type { ApiError, AvatarSize, StatusDotKind, Toast, ToastInput, User } from './types'

export function useToasts() {
  const [toasts, setToasts] = React.useState<Toast[]>([])
  const push = React.useCallback((toast: ToastInput) => {
    const id = Math.random().toString(36).slice(2)
    setToasts(items => [...items, { id, ...toast }])
    setTimeout(() => setToasts(items => items.filter(item => item.id !== id)), toast.duration || 5000)
  }, [])
  return { toasts, push }
}

export function ToastHost({ toasts }: { toasts: Toast[] }) {
  return (
    <div className="toast-host">
      {toasts.map(toast => (
        <div key={toast.id} className={`toast ${toast.variant || 'info'}`}>
          <Icon name={toast.variant === 'success' ? 'check-circle' : toast.variant === 'error' ? 'x-circle' : toast.variant === 'warn' ? 'alert-triangle' : 'info'} size={16} style={{ marginTop: 1, color: `var(--${toast.variant === 'success' ? 'ok' : toast.variant === 'error' ? 'err' : toast.variant === 'warn' ? 'warn' : 'info'})` }} />
          <div className="toast-body">
            <div className="toast-title">{toast.title}</div>
            {toast.sub && <div className={`toast-sub ${toast.mono ? 'mono' : ''}`}>{toast.sub}</div>}
          </div>
        </div>
      ))}
    </div>
  )
}

interface ModalProps {
  open: boolean
  onClose?: () => void
  children: React.ReactNode
}

export function Modal({ open, onClose, children }: ModalProps) {
  React.useEffect(() => {
    if (!open) return
    const handleKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && onClose?.()
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={event => event.stopPropagation()}>{children}</div>
    </div>
  )
}

export function MCAvatar({ name, size = 'md', uuid }: { name: string; size?: AvatarSize; uuid?: string | null }) {
  const color = avatarColor(name || '?')
  const letter = (name || '?').slice(0, 2).toUpperCase()
  const cls = size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : ''
  const lookup = (uuid && uuid.trim()) || (name && name.trim()) || ''
  const [failed, setFailed] = React.useState(false)
  const px = size === 'sm' ? 40 : size === 'lg' ? 72 : 56
  const showSkin = !!lookup && !failed
  return (
    <div className={`mc-avatar ${cls}`} style={{ background: color }} title={name}>
      {showSkin ? (
        <img
          className="mc-avatar-skin"
          src={`https://minotar.net/helm/${encodeURIComponent(lookup)}/${px}.png`}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        letter
      )}
    </div>
  )
}

export function Avatar({ user, size = 20 }: { user?: User | null; size?: number }) {
  if (!user) return <span className="dim">--</span>
  const bg = user.accentColor || avatarColor(user.displayName || user.username)
  return (
    <div
      className="user-avatar"
      style={{ width: size, height: size, background: bg, fontSize: size * 0.45 }}
      title={user.displayName}
    >
      {initials(user.displayName || user.username)}
    </div>
  )
}

export function StatusDot({ kind }: { kind: StatusDotKind }) {
  return <span className={`status-dot ${kind}`} />
}

interface SparklineProps {
  data: number[]
  color?: string
  fill?: boolean
  height?: number
}

export function Sparkline({ data, color = 'var(--text-dim)', fill = true, height = 28 }: SparklineProps) {
  if (!data || data.length === 0) {
    return <div className="spark empty" style={{ height }} />
  }
  const max = Math.max(...data) * 1.1
  const min = Math.min(...data) * 0.9
  const range = max - min || 1
  const points = data.map((value, index) => {
    const x = (index / Math.max(1, data.length - 1)) * 100
    const y = 100 - ((value - min) / range) * 100
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  return (
    <svg className="spark" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ height }}>
      {fill && (
        <polygon
          points={`0,100 ${points.join(' ')} 100,100`}
          fill={color}
          opacity="0.15"
        />
      )}
      <polyline points={points.join(' ')} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

export function Bar({ pct, color = 'var(--accent)' }: { pct: number; color?: string }) {
  return (
    <div style={{ height: 4, background: 'var(--surface-2)', borderRadius: 2, overflow: 'hidden', width: '100%' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color }} />
    </div>
  )
}

interface DataStateProps {
  loading: boolean
  error: ApiError | null
  empty?: boolean
  emptyLabel?: string
  onRetry?: () => void
  children: React.ReactNode
}

export function DataState({ loading, error, empty, emptyLabel, onRetry, children }: DataStateProps) {
  if (error) {
    return (
      <div className="card data-state error">
        <div className="data-state-icon">
          <Icon name="alert-triangle" size={18} style={{ color: 'var(--err)' }} />
        </div>
        <div className="data-state-body">
          <div className="data-state-title">Could not load</div>
          <div className="data-state-sub">{error.message}</div>
        </div>
        {onRetry && (
          <button className="btn sm" onClick={onRetry}>
            <Icon name="refresh-cw" size={12} /> Retry
          </button>
        )}
      </div>
    )
  }
  if (loading) {
    return (
      <div className="card data-state loading">
        <div className="skeleton-line" />
        <div className="skeleton-line short" />
      </div>
    )
  }
  if (empty) {
    return (
      <div className="card data-state empty">
        <div className="dim">{emptyLabel || 'Nothing here yet.'}</div>
      </div>
    )
  }
  return <>{children}</>
}

export interface DropdownOption {
  value: string
  label: React.ReactNode
}

interface DropdownProps {
  value: string
  onChange: (value: string) => void
  options: DropdownOption[]
  disabled?: boolean
  className?: string
  style?: React.CSSProperties
  size?: 'sm'
  placeholder?: string
}

export function Dropdown({ value, onChange, options, disabled, className, style, size, placeholder }: DropdownProps) {
  const [open, setOpen] = React.useState(false)
  const [highlighted, setHighlighted] = React.useState<number>(-1)
  const [menuRect, setMenuRect] = React.useState<{ top: number; left: number; width: number; dropUp: boolean } | null>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)
  const optionRefs = React.useRef<Array<HTMLDivElement | null>>([])

  const selectedIndex = options.findIndex(opt => opt.value === value)
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null

  const positionMenu = React.useCallback(() => {
    const button = buttonRef.current
    if (!button) return
    const rect = button.getBoundingClientRect()
    const menuMaxHeight = 280
    const spaceBelow = window.innerHeight - rect.bottom
    const dropUp = spaceBelow < menuMaxHeight + 12 && rect.top > spaceBelow
    setMenuRect({
      top: dropUp ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      dropUp,
    })
  }, [])

  React.useEffect(() => {
    if (!open) return
    positionMenu()
    const handlePointer = (event: MouseEvent) => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const handleScrollOrResize = () => positionMenu()
    window.addEventListener('mousedown', handlePointer)
    window.addEventListener('resize', handleScrollOrResize)
    window.addEventListener('scroll', handleScrollOrResize, true)
    return () => {
      window.removeEventListener('mousedown', handlePointer)
      window.removeEventListener('resize', handleScrollOrResize)
      window.removeEventListener('scroll', handleScrollOrResize, true)
    }
  }, [open, positionMenu])

  React.useEffect(() => {
    if (open) setHighlighted(selectedIndex >= 0 ? selectedIndex : 0)
  }, [open, selectedIndex])

  React.useEffect(() => {
    if (!open) return
    const node = optionRefs.current[highlighted]
    if (node) node.scrollIntoView({ block: 'nearest' })
  }, [highlighted, open])

  function commit(idx: number) {
    if (idx < 0 || idx >= options.length) return
    onChange(options[idx].value)
    setOpen(false)
    buttonRef.current?.focus()
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (disabled) return
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        setOpen(true)
      }
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      buttonRef.current?.focus()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlighted(idx => Math.min(idx + 1, options.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlighted(idx => Math.max(idx - 1, 0))
    } else if (event.key === 'Home') {
      event.preventDefault()
      setHighlighted(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      setHighlighted(options.length - 1)
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      commit(highlighted)
    } else if (event.key === 'Tab') {
      setOpen(false)
    }
  }

  const cls = ['dropdown', size === 'sm' ? 'sm' : '', open ? 'open' : '', className || ''].filter(Boolean).join(' ')

  return (
    <div className={cls} style={style}>
      <button
        ref={buttonRef}
        type="button"
        className="dropdown-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={handleKeyDown}
      >
        <span className="dropdown-label">
          {selectedOption ? selectedOption.label : <span className="dim">{placeholder || ' '}</span>}
        </span>
        <span className="dropdown-chevron" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="2.5,4.5 6,8 9.5,4.5" />
          </svg>
        </span>
      </button>
      {open && menuRect && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className={`dropdown-menu${size === 'sm' ? ' sm' : ''}${menuRect.dropUp ? ' drop-up' : ''}`}
          role="listbox"
          style={{
            top: menuRect.dropUp ? undefined : menuRect.top,
            bottom: menuRect.dropUp ? window.innerHeight - menuRect.top : undefined,
            left: menuRect.left,
            minWidth: menuRect.width,
          }}
          onKeyDown={handleKeyDown}
        >
          {options.map((option, idx) => (
            <div
              key={option.value}
              ref={node => { optionRefs.current[idx] = node }}
              className={['dropdown-option', idx === highlighted ? 'highlighted' : '', option.value === value ? 'selected' : ''].filter(Boolean).join(' ')}
              role="option"
              aria-selected={option.value === value}
              onMouseEnter={() => setHighlighted(idx)}
              onMouseDown={event => event.preventDefault()}
              onClick={() => commit(idx)}
            >
              <span className="dropdown-option-label">{option.label}</span>
              {option.value === value && (
                <Icon name="check" size={12} className="dropdown-option-check" />
              )}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}
